import React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  TextField,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  contentList,
  contentRead,
  contentWrite,
  createPreviewPR,
} from "../../services/content";

const MigrationAdmin: React.FC = () => {
  const [products, setProducts] = React.useState<string[]>([]);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [log, setLog] = React.useState<string>("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [publishMode, setPublishMode] = React.useState<"prod" | "preview">(
    "preview"
  );
  const [commitMsg, setCommitMsg] = React.useState(
    "chore(products): migrate legacy schema to v1"
  );
  const [branchName, setBranchName] = React.useState("");
  const [prUrl, setPrUrl] = React.useState("");

  React.useEffect(() => {
    (async () => {
      try {
        const list = await contentList("data/products");
        const slugs = (list.items || [])
          .filter((i) => i.type === "dir")
          .map((i) => i.name)
          .filter((name) => name && !name.startsWith("."));
        setProducts(slugs);
      } catch (e: any) {
        setError(e?.message || "Liste alınamadı");
      }
    })();
  }, []);

  const normalize = (json: any): any => {
    const p = { ...(json || {}) };
    const locales = p.locales || { tr: {}, en: {} };
    if (!locales.tr.title && p.title) locales.tr.title = p.title;
    if (!locales.tr.subtitle && p.subtitle) locales.tr.subtitle = p.subtitle;
    if (!locales.tr.description && p.description)
      locales.tr.description = p.description;
    const media = p.media || {};
    if (!media.images && Array.isArray(p.images)) media.images = p.images;
    if (!media.model3d && p.model3d) media.model3d = p.model3d;
    if (!media.catalogPdf && p.catalogPdf) media.catalogPdf = p.catalogPdf;
    const pricing = p.pricing || {};
    if (typeof pricing.price === "undefined" && typeof p.price !== "undefined")
      pricing.price = p.price;
    if (
      typeof pricing.showPrice === "undefined" &&
      typeof p.showPrice !== "undefined"
    )
      pricing.showPrice = p.showPrice;
    const next = {
      slug: p.slug,
      category: p.category || "",
      fireRating: p.fireRating || "",
      locales,
      media,
      pricing: { currency: "TRY", ...pricing },
    };
    return next;
  };

  const migrateSelected = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      setLog("");
      const branch =
        publishMode === "preview"
          ? branchName || `preview/migrate-${Date.now()}`
          : undefined;
      for (const slug of selected) {
        try {
          const res = await contentRead(`data/products/${slug}/index.json`);
          let json: any = {};
          try {
            json = JSON.parse(atob(res.content || ""));
          } catch {}
          const next = normalize(json);
          await contentWrite(
            `data/products/${slug}/index.json`,
            JSON.stringify(next, null, 2),
            `${commitMsg} ${slug}`,
            branch
          );
          setLog((l) => l + `✓ ${slug} migrated\n`);
        } catch (e: any) {
          setLog((l) => l + `✗ ${slug} failed: ${e?.message || e}\n`);
        }
      }
      if (branch) {
        const pr = await createPreviewPR(
          branch,
          "Migrate Products",
          "Automated schema migration"
        );
        setPrUrl(pr.url);
      }
      setSuccess("Görev tamamlandı");
    } catch (e: any) {
      setError(e?.message || "Hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>
          Migration (Legacy → v1)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Ürünler
                </Typography>
                <Stack spacing={1}>
                  {products.map((slug) => (
                    <Button
                      key={slug}
                      variant={
                        selected.includes(slug) ? "contained" : "outlined"
                      }
                      onClick={() =>
                        setSelected((s) =>
                          s.includes(slug)
                            ? s.filter((x) => x !== slug)
                            : [...s, slug]
                        )
                      }
                    >
                      {slug}
                    </Button>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Ayarlar
                </Typography>
                <Stack spacing={1}>
                  <TextField
                    size="small"
                    label="Commit Mesajı"
                    value={commitMsg}
                    onChange={(e) => setCommitMsg(e.target.value)}
                  />
                  <TextField
                    size="small"
                    label="Yayın Modu"
                    select
                    SelectProps={{ native: true }}
                    value={publishMode}
                    onChange={(e) => setPublishMode(e.target.value as any)}
                  >
                    <option value="preview">Preview PR</option>
                    <option value="prod">Prod</option>
                  </TextField>
                  {publishMode === "preview" && (
                    <TextField
                      size="small"
                      label="Branch"
                      value={branchName}
                      onChange={(e) => setBranchName(e.target.value)}
                      placeholder={`preview/migrate-${Date.now()}`}
                    />
                  )}
                  <Button
                    variant="contained"
                    onClick={migrateSelected}
                    disabled={loading || selected.length === 0}
                  >
                    Seçilenleri Taşı ({selected.length})
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Kayıt
            </Typography>
            <Box
              component="pre"
              sx={{
                bgcolor: "#111",
                color: "#eee",
                p: 2,
                borderRadius: 1,
                height: 200,
                overflow: "auto",
              }}
            >
              {log}
            </Box>
          </CardContent>
        </Card>

        <Snackbar
          open={!!error}
          onClose={() => setError("")}
          autoHideDuration={5000}
        >
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!success}
          onClose={() => setSuccess("")}
          autoHideDuration={3000}
        >
          <Alert severity="success" onClose={() => setSuccess("")}>
            {success}
            {prUrl && (
              <Button
                component="a"
                href={prUrl}
                target="_blank"
                size="small"
                sx={{ ml: 1 }}
              >
                PR'ı Aç
              </Button>
            )}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default MigrationAdmin;
